"use strict";
import React, {
    useContext,
    useEffect,
    useState,
    useRef,
    useMemo,
} from "react";
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListSubheader,
    Typography,
    TextField,
    Tooltip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import axios from "axios";
import {
    GET_PROJECT_SUMMARIES,
    GET_AUDIOBOOKS,
    GET_PROJECTS_FOR_SINGLE_USER,
    GET_AUDIOBOOK_DETAILS,
} from "../ApiEndpoints.js";
import { Store } from "../Store.js";
import { getLanguageName } from "../utils/generalFunctions.js";
import { getLocalStorageItem } from "../utils/storage.js";
import { getProjectById } from "../features/projects/api.js";
import { getWithFirebaseAuth } from "../utils/firebaseApiClient.js";

import {
    videoAdminRoles,
    audioAdminRoles,
    videoRoleOrder,
    audioRoleOrder,
    roleLabels,
    languageSpecificRoles,
    roleEmojis,
    isVisibleVideoRole,
} from "../Data/tasksCode.js";

const GO_EMOJI = "➡️";
const ANALYZE_EMOJI = "📊";
const ADMIN_ACTIONS_EMOJI = "⚙️";

const getAdminActionsPath = (project) =>
    project.type === "audio"
        ? `/audiobooks/actions/${project.id}`
        : `/actions/${project.id}`;
// Roles that require lipsync to be enabled
const LIPSYNC_ROLES = new Set([
    "lipqualityassurance",
    "lipsqa",
]);

const ProjectSidebar = () => {
    const navigate = useNavigate();
    const { state, dispatch: ctxDispatch } = useContext(Store);
    const { userInfo } = state;
    const [collapsed, setCollapsed] = useState(() => {
        const stored = localStorage.getItem("projectSidebarCollapsed");
        return stored === null ? false : stored === "true";
    });
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sidebarHeight, setSidebarHeight] = useState(null);
    const [openProjects, setOpenProjects] = useState({});
    const [openRoles, setOpenRoles] = useState({});
    const [activeProjectId, setActiveProjectId] = useState(null);
    const [activeRole, setActiveRole] = useState(null);
    const [activeLanguage, setActiveLanguage] = useState(null);
    const [truncatedProjectNames, setTruncatedProjectNames] = useState({});
    const projectRefs = useRef({});
    const projectNameRefs = useRef({});
    const roleRefs = useRef({});
    const languageRefs = useRef({});
    const location = useLocation();
    // Track if we've already scrolled to the active item after navigation
    const hasScrolledRef = useRef(false);

    // Persist the collapsed state across sessions
    useEffect(() => {
        localStorage.setItem(
            "projectSidebarCollapsed",
            collapsed ? "true" : "false"
        );
    }, [collapsed]);

    const toSafeDate = (input) => {
        if (!input) return null;

        // Already a Date and valid
        if (input instanceof Date && !isNaN(input)) return input;

        // Firestore-like {seconds, nanoseconds}
        if (typeof input === "object" && input.seconds != null) {
            const ms = input.seconds * 1000 + (input.nanoseconds || 0) / 1_000_000;
            const d = new Date(ms);
            return isNaN(d) ? null : d;
        }

        // Number (epoch seconds or ms)
        if (typeof input === "number") {
            const ms = input > 1e12 ? input : input * 1000;
            const d = new Date(ms);
            return isNaN(d) ? null : d;
        }

        // ISO or other string
        if (typeof input === "string") {
            let d = new Date(input);
            return isNaN(d) ? null : d;
        }

        return null;
    };

    // Check whether a project name is visually truncated before enabling tooltip
    const checkProjectNameTruncation = (projectId) => {
        const nameElement = projectNameRefs.current[projectId];
        if (!nameElement) {
            return;
        }

        const isTruncated = nameElement.scrollWidth > nameElement.clientWidth;
        setTruncatedProjectNames((prev) => {
            if (prev[projectId] === isTruncated) {
                return prev;
            }
            return { ...prev, [projectId]: isTruncated };
        });
    };

    // Group projects by creation month for display
    const groupedProjects = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const formatter = new Intl.DateTimeFormat("en-US", { month: "long" });

        const query = searchTerm.trim().toLowerCase();

        // Ensure dates are valid and sort newest-first
        const safe = projects
            .map((p) => ({ ...p, _createdAt: toSafeDate(p.createdAt) }))
            .filter((p) => p._createdAt) // drop invalid/null
            .filter(
                (p) => !query || (p.name || "").toLowerCase().includes(query)
            )
            .sort((a, b) => b._createdAt - a._createdAt);

        const groups = [];
        let lastLabel = null;

        safe.forEach((p) => {
            const date = p._createdAt;
            const label =
                formatter.format(date) +
                (date.getFullYear() === currentYear ? "" : ` ${date.getFullYear()}`);

            if (label !== lastLabel) {
                groups.push({ label, items: [] });
                lastLabel = label;
            }
            // keep original shape for render
            groups[groups.length - 1].items.push(p);
        });

        return groups;
    }, [projects, searchTerm]);


    const toggleProject = (id) => {
        setOpenProjects((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleLanguageClick = async (project, role, language) => {
        setLoading(true);
        try {
            let projectDetails = project;
            if (project.type === "audio") {
                const { data } = await axios.get(
                    `${GET_AUDIOBOOK_DETAILS}/${project.id}`
                );
                projectDetails = data.project;
            } else {
                const response = await getProjectById(project.id);
                projectDetails = response.data;
            }
            ctxDispatch({ type: "PROJECT_FILE", payload: projectDetails });
            localStorage.setItem(
                "projectFile",
                JSON.stringify(projectDetails)
            );
            const normalizeRole = (r) => {
                switch (r) {
                    case "transcriber":
                        return "transcribe";
                    case "translator":
                        return "translate";
                    case "performancedirector":
                        return "performancedirection";
                    case "lipsqa":
                        return "lipqualityassurance";
                    case "qa":
                        return "qualityassurance";
                    default:
                        return r;
                }
            };
            const normalized = normalizeRole(role);
            setLoading(false);
            if (project.type === "audio") {
                if (normalized === "performancedirection") {
                    navigate(
                        `/audiobooks/performancedirection/${project.id}/${language}`
                    );
                } else if (
                    normalized === "finalqa" ||
                    normalized === "qualityassurance"
                ) {
                    navigate(`/audiobooks/finalqa/${project.id}/${language}`);
                }
            } else {
                if (normalized === "transcribe") {
                    navigate("/work/transcriber");
                } else if (normalized === "translate") {
                    navigate(`/work/translator/${language}`);
                } else if (normalized === "performancedirection") {
                    navigate(`/work/performancedirection/${language}`);
                } else if (normalized === "performancedirectionversions") {
                    navigate(`/work/performancedirectionversions/${language}`);
                } else if (normalized === "attribution") {
                    navigate("/work/attribution");
                } else if (normalized === "lipqualityassurance") {
                    navigate(`/work/lipqualityassurance/${language}`);
                } else if (
                    normalized === "qualityassurance" ||
                    normalized === "finalqa"
                ) {
                    navigate(`/work/qualityassurance/${language}`);
                }
            }
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const toggleRole = (projectId, role) => {
        setOpenRoles((prev) => ({
            ...prev,
            [projectId]: {
                ...prev[projectId],
                [role]: !prev[projectId]?.[role],
            },
        }));
    };

    useEffect(() => {
        // Wait until userInfo is ready
        if (!userInfo) return;

        let isMounted = true;

        const load = async () => {
            try {
                setLoading(true);

                // helper to normalize a project shape (uses the toSafeDate defined above)
                const mapProject = (p, typeOverride) => {
                    const type =
                        typeOverride ||
                        (p.type === "audiobook" || p.type === "audio" ? "audio" : "video");
                    const languages = p.finalLang || p.finalLanguages || [];
                    const lipsyncEnabled = Boolean(p.lipsyncEnabled || p.lipsync || p.lipSync);

                    return {
                        id: p.id,
                        name: p.name,
                        type,
                        languages,
                        createdAt: toSafeDate(p.creationDate) || toSafeDate(p.createdAt) || null,
                        lipsyncEnabled,
                    };
                };

                if (userInfo.isAdmin) {
                    // ADMIN: fetch video + audio
                    const [videoRes, audioRes] = await Promise.all([
                        getWithFirebaseAuth(GET_PROJECT_SUMMARIES),
                        axios.get(GET_AUDIOBOOKS),
                    ]);

                    const videoProjects =
                        videoRes.data.projectsArray?.map((p) => ({
                            ...mapProject(p, "video"),
                            roles: [...videoAdminRoles],
                        })) || [];

                    const audioProjects =
                        audioRes.data.data?.audiobooks?.map((p) => ({
                            ...mapProject(p, "audio"),
                            roles: [...audioAdminRoles],
                        })) || [];

                    // Remove lipsync-only roles where lipsync not enabled, drop bad dates, sort newest first
                    const all = [...videoProjects, ...audioProjects]
                        .map((proj) => {
                            if (!proj.lipsyncEnabled) {
                                proj.roles = proj.roles.filter((r) => !LIPSYNC_ROLES.has(r));
                            }
                            return proj;
                        })
                        .filter((p) => p.createdAt)
                        .sort((a, b) => b.createdAt - a.createdAt);

                    if (isMounted) setProjects(all);
                } else {
                    // NON-ADMIN: user’s assignments (video + audio unified)
                    const { data } = await axios.post(GET_PROJECTS_FOR_SINGLE_USER, {
                        id: userInfo.id,
                        searchTerm: "",
                    });

                    const projectMap = new Map();
                    data.projectsArray?.forEach((p) => {
                        const base = mapProject(p);
                        const roles = Array.isArray(p.role)
                            ? p.role
                            : typeof p.role === "string" && p.role !== "unknown"
                            ? [p.role]
                            : [];
                        const existing = projectMap.get(base.id);

                        if (existing) {
                            // merge languages and roles
                            existing.languages = Array.from(
                                new Set([...existing.languages, ...base.languages])
                            );
                            roles.forEach((r) => {
                                if (r && r !== "unknown" && !existing.roles.includes(r)) {
                                    existing.roles.push(r);
                                }
                            });
                        } else {
                            projectMap.set(base.id, {
                                ...base,
                                roles: roles.filter((r) => r && r !== "unknown"),
                            });
                        }
                    });

                    const items = Array.from(projectMap.values())
                        .filter((p) => p.createdAt)
                        .map((project) => {
                            if (project.type !== "audio") {
                                project.roles = project.roles.filter(isVisibleVideoRole);
                            }
                            const order = project.type === "audio" ? audioRoleOrder : videoRoleOrder;
                            project.roles.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                            return project;
                        })
                        .sort((a, b) => b.createdAt - a.createdAt);

                    if (isMounted) setProjects(items);
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        load();
        return () => {
            isMounted = false;
        };
    }, [userInfo, userInfo?.id, userInfo?.isAdmin]);



    useEffect(() => {
        // Parse the current path to determine active project, role, and language
        const path = location.pathname;
        let projectId = null;
        let roleCandidates = [];
        let language = null;

        // Handle audiobook routes
        if (path.startsWith("/audiobooks/")) {
            const parts = path.split("/");
            const roleSeg = parts[2];
            projectId = parts[3];
            language = parts[4] || null;
            if (roleSeg === "performancedirection") {
                roleCandidates = ["performancedirection", "performancedirector"];
            } else if (roleSeg === "finalqa") {
                roleCandidates = ["finalqa", "qualityassurance", "qa"];
            }
        } else if (path.startsWith("/work/")) {
            // Handle generic work routes
            const parts = path.split("/");
            const roleSeg = parts[2];
            language = parts[3] || null;
            const projectFile =
                state.projectFile ||
                getLocalStorageItem("projectFile", null);
            projectId = projectFile?.id || null;
            switch (roleSeg) {
                case "transcriber":
                    roleCandidates = ["transcriber", "transcribe"];
                    break;
                case "translator":
                    roleCandidates = ["translator", "translate"];
                    break;
                case "performancedirection":
                    roleCandidates = [
                        "performancedirection",
                        "performancedirector",
                    ];
                    break;
                case "performancedirectionversions":
                    roleCandidates = ["performancedirectionversions"];
                    break;
                case "attribution":
                    roleCandidates = ["attribution", "attributioneditor"];
                    break;
                case "lipqualityassurance":
                    roleCandidates = ["lipqualityassurance", "lipsqa"];
                    break;
                case "qualityassurance":
                    roleCandidates = [
                        "qualityassurance",
                        "qa",
                        "finalqa",
                    ];
                    break;
                default:
                    roleCandidates = [];
            }
        }

        // Update active state and expand sidebar sections
        if (projectId) {
            setActiveProjectId(projectId);
            setActiveLanguage(language);
            const project = projects.find((p) => p.id === projectId);
            let matchedRole = null;
            if (project) {
                matchedRole = roleCandidates.find((r) =>
                    project.roles.includes(r)
                );
            }
            matchedRole = matchedRole || roleCandidates[0] || null;
            setActiveRole(matchedRole);
            setOpenProjects((prev) => ({ ...prev, [projectId]: true }));
            if (matchedRole && languageSpecificRoles.has(matchedRole)) {
                setOpenRoles((prev) => ({
                    ...prev,
                    [projectId]: {
                        ...prev[projectId],
                        [matchedRole]: true,
                    },
                }));
            }
        } else {
            // Clear active state if no matching project is found
            setActiveProjectId(null);
            setActiveRole(null);
            setActiveLanguage(null);
        }
    }, [location.pathname, projects, state.projectFile]);

    useEffect(() => {
        // Reset scroll flag when navigation changes the active identifiers
        hasScrolledRef.current = false;
    }, [activeProjectId, activeRole, activeLanguage]);

    useEffect(() => {
        // Locate the active element and ensure it is visible in the sidebar
        let target = null;
        if (activeProjectId && activeRole && activeLanguage) {
            target =
                languageRefs.current[
                `${activeProjectId}-${activeRole}-${activeLanguage}`
                ];
        }
        if (!target && activeProjectId && activeRole) {
            target = roleRefs.current[`${activeProjectId}-${activeRole}`];
        }
        if (!target && activeProjectId) {
            target = projectRefs.current[activeProjectId];
        }
        if (target && target.scrollIntoView && !hasScrolledRef.current) {
            target.scrollIntoView({ block: "nearest" });
            hasScrolledRef.current = true;
        }
    }, [
        activeProjectId,
        activeRole,
        activeLanguage,
        openProjects,
        openRoles,
    ]);

    useEffect(() => {
        // Fill the window below whatever part of the header is still visible,
        // so the sticky sidebar grows as the header scrolls out of view.
        const updateHeight = () => {
            const header = document.getElementById("header");
            const visibleHeaderHeight = header
                ? Math.max(0, header.getBoundingClientRect().bottom)
                : 0;
            setSidebarHeight(window.innerHeight - visibleHeaderHeight);
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        window.addEventListener("scroll", updateHeight, { passive: true });
        return () => {
            window.removeEventListener("resize", updateHeight);
            window.removeEventListener("scroll", updateHeight);
        };
    }, []);

    if (!userInfo) {
        return null;
    }

    return (
        <Box
            sx={{
                width: collapsed ? 40 : 250,
                transition: "width 0.3s",
                bgcolor: "var(--color-dropdown-bg)",
                borderRight: "1px solid #ddd",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: sidebarHeight ?? "auto",
                maxHeight: sidebarHeight ?? "auto",
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    p: 1,
                    borderBottom: "1px solid #ddd",
                }}
            >
                {!collapsed && (
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ fontFamily: "var(--font-heading)" }}
                    >
                        Projects
                    </Typography>
                )}
                <IconButton
                    size="small"
                    onClick={() => setCollapsed(!collapsed)}
                    sx={{ ml: collapsed ? 0 : 1 }}
                >
                    {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Box>
            {!collapsed && (
                <Box sx={{ p: 1, borderBottom: "1px solid #ddd" }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search projects"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") setSearchTerm("");
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="Clear search"
                                        onClick={() => setSearchTerm("")}
                                        edge="end"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            )}
            {!collapsed && (
                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                    {loading ? (
                        <Typography sx={{ p: 1 }}>Loading...</Typography>
                    ) : searchTerm.trim() && groupedProjects.length === 0 ? (
                        <Typography variant="body2" sx={{ p: 1 }}>
                            No matching projects
                        </Typography>
                    ) : (
                        <List dense subheader={<li />}>
                            {groupedProjects.map(({ label, items }) => (
                                <li key={label}>
                                    <ul style={{ padding: 0 }}>
                                        <ListSubheader
                                            sx={{
                                                bgcolor: "var(--color-dropdown-bg)",
                                                pl: 1,
                                                fontWeight: "bold",
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 1,
                                            }}
                                        >
                                            {label}
                                        </ListSubheader>
                                        {items.map((project) => (
                                            <React.Fragment key={project.id}>
                                                <ListItem
                                                    button
                                                    ref={(el) =>
                                                    (projectRefs.current[project.id] =
                                                        el)
                                                    }
                                                    onClick={() => toggleProject(project.id)}
                                                    sx={{
                                                        py: 0.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        cursor: "pointer",
                                                        bgcolor:
                                                            activeProjectId === project.id
                                                                ? "var(--color-dropdown-hover-bg)"
                                                                : "inherit",
                                                    }}
                                                >
                                                    {project.type === "audio" ? (
                                                        <VolumeUpOutlinedIcon
                                                            fontSize="small"
                                                            sx={{ mr: 1 }}
                                                        />
                                                    ) : (
                                                        <MovieOutlinedIcon
                                                            fontSize="small"
                                                            sx={{ mr: 1 }}
                                                        />
                                                    )}
                                                    <Tooltip
                                                        title={
                                                            truncatedProjectNames[project.id]
                                                                ? project.name
                                                                : ""
                                                        }
                                                        placement="top"
                                                        disableHoverListener={
                                                            !truncatedProjectNames[project.id]
                                                        }
                                                        disableFocusListener={
                                                            !truncatedProjectNames[project.id]
                                                        }
                                                        disableTouchListener={
                                                            !truncatedProjectNames[project.id]
                                                        }
                                                    >
                                                        <Typography
                                                            noWrap
                                                            sx={{ flex: 1 }}
                                                            ref={(el) =>
                                                                (projectNameRefs.current[
                                                                    project.id
                                                                ] = el)
                                                            }
                                                            onMouseEnter={() =>
                                                                checkProjectNameTruncation(
                                                                    project.id
                                                                )
                                                            }
                                                            onFocus={() =>
                                                                checkProjectNameTruncation(
                                                                    project.id
                                                                )
                                                            }
                                                        >
                                                            {project.name}
                                                        </Typography>
                                                    </Tooltip>
                                                </ListItem>
                                                {openProjects[project.id] && (
                                                    <List component="div" disablePadding sx={{ pl: 4 }}>
                                                        {userInfo?.isAdmin && (
                                                            <ListItem
                                                                key="manage"
                                                                sx={{
                                                                    py: 0.25,
                                                                    cursor: "pointer",
                                                                    bgcolor:
                                                                        location.pathname ===
                                                                        `/projects/${project.id}/manage`
                                                                            ? "var(--color-dropdown-hover-bg)"
                                                                            : "inherit",
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/projects/${project.id}/manage`
                                                                    )
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            marginRight:
                                                                                4,
                                                                        }}
                                                                    >
                                                                        🛠️
                                                                    </span>
                                                                    <span
                                                                        style={{
                                                                            textDecoration:
                                                                                "underline",
                                                                        }}
                                                                    >
                                                                        Manage
                                                                    </span>
                                                                </Typography>
                                                            </ListItem>
                                                        )}
                                                          {project.roles.map((role) => {
                                                              const label = roleLabels[role] || role;
                                                              const emoji = roleEmojis[role];
                                                              const isLangSpecific =
                                                                  languageSpecificRoles.has(role);
                                                              return (
                                                                  <React.Fragment key={role}>
                                                                    <ListItem
                                                                        ref={(el) =>
                                                                        (roleRefs.current[
                                                                            `${project.id}-${role}`
                                                                        ] = el)
                                                                        }
                                                                        sx={{
                                                                            py: 0.25,
                                                                            cursor: "pointer",
                                                                            bgcolor:
                                                                                activeProjectId ===
                                                                                    project.id &&
                                                                                    activeRole ===
                                                                                    role &&
                                                                                    !activeLanguage
                                                                                    ? "var(--color-dropdown-hover-bg)"
                                                                                    : "inherit",
                                                                        }}
                                                                        onClick={() =>
                                                                            isLangSpecific
                                                                                ? toggleRole(
                                                                                    project.id,
                                                                                    role
                                                                                )
                                                                                : handleLanguageClick(
                                                                                    project,
                                                                                    role
                                                                                )
                                                                        }
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                            }}
                                                                        >
                                                                            {emoji && (
                                                                                <span style={{ marginRight: 4 }}>
                                                                                    {emoji}
                                                                                </span>
                                                                            )}
                                                                            <span
                                                                                style={{
                                                                                    textDecoration: isLangSpecific
                                                                                        ? "none"
                                                                                        : "underline",
                                                                                }}
                                                                            >
                                                                                {label}
                                                                            </span>
                                                                        </Typography>
                                                                    </ListItem>
                                                                    {isLangSpecific &&
                                                                        openRoles[project.id]?.[role] && (
                                                                            <List
                                                                                component="div"
                                                                                disablePadding
                                                                                sx={{ pl: 4 }}
                                                                            >
                                                                                {project.languages.map(
                                                                                    (lang) => (
                                                                                        <ListItem
                                                                                            key={lang}
                                                                                            ref={(el) =>
                                                                                            (languageRefs.current[
                                                                                                `${project.id}-${role}-${lang}`
                                                                                            ] =
                                                                                                el)
                                                                                            }
                                                                                            sx={{
                                                                                                py: 0.25,
                                                                                                display: "flex",
                                                                                                alignItems: "center",
                                                                                                bgcolor:
                                                                                                    activeProjectId ===
                                                                                                        project.id &&
                                                                                                        activeRole ===
                                                                                                        role &&
                                                                                                        activeLanguage ===
                                                                                                        lang
                                                                                                        ? "var(--color-dropdown-hover-bg)"
                                                                                                        : "inherit",
                                                                                            }}
                                                                                        >
                                                                                            <Typography
                                                                                                variant="body2"
                                                                                                sx={{
                                                                                                    display: "flex",
                                                                                                    alignItems: "center",
                                                                                                }}
                                                                                            >
                                                                                                <span style={{ marginRight: 4 }}>
                                                                                                    {GO_EMOJI}
                                                                                                </span>
                                                                                                <span
                                                                                                    style={{
                                                                                                        cursor: "pointer",
                                                                                                        textDecoration: "underline",
                                                                                                    }}
                                                                                                    onClick={() =>
                                                                                                        handleLanguageClick(
                                                                                                            project,
                                                                                                            role,
                                                                                                            lang
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    {getLanguageName(lang)}
                                                                                                </span>
                                                                                            </Typography>
                                                                                        </ListItem>
                                                                                    )
                                                                                )}
                                                                            </List>
                                                                        )}
                                                                  </React.Fragment>
                                                              );
                                                          })}
                                                          <ListItem
                                                              key="analyze"
                                                              sx={{
                                                                  py: 0.25,
                                                                  cursor: "pointer",
                                                                  bgcolor:
                                                                      location.pathname ===
                                                                      (project.type === "audio"
                                                                          ? `/audiobooks/details/${project.id}`
                                                                          : `/workpages/details/${project.id}`)
                                                                          ? "var(--color-dropdown-hover-bg)"
                                                                          : "inherit",
                                                              }}
                                                              onClick={() =>
                                                                  navigate(
                                                                      project.type === "audio"
                                                                          ? `/audiobooks/details/${project.id}`
                                                                          : `/workpages/details/${project.id}`
                                                                  )
                                                              }
                                                          >
                                                              <Typography
                                                                  variant="body2"
                                                                  sx={{
                                                                      display: "flex",
                                                                      alignItems: "center",
                                                                  }}
                                                              >
                                                                  <span
                                                                      style={{ marginRight: 4 }}
                                                                  >
                                                                      {ANALYZE_EMOJI}
                                                                  </span>
                                                                  <span
                                                                      style={{
                                                                          textDecoration:
                                                                              "underline",
                                                                      }}
                                                                  >
                                                                      Analyze
                                                                  </span>
                                                              </Typography>
                                                          </ListItem>
                                                          {userInfo?.isAdmin && (
                                                              <ListItem
                                                                  key="admin-actions"
                                                                  sx={{
                                                                      py: 0.25,
                                                                      cursor: "pointer",
                                                                      bgcolor:
                                                                          location.pathname ===
                                                                          getAdminActionsPath(project)
                                                                              ? "var(--color-dropdown-hover-bg)"
                                                                              : "inherit",
                                                                  }}
                                                                  onClick={() =>
                                                                      navigate(getAdminActionsPath(project))
                                                                  }
                                                              >
                                                                  <Typography
                                                                      variant="body2"
                                                                      sx={{
                                                                          display: "flex",
                                                                          alignItems: "center",
                                                                      }}
                                                                  >
                                                                      <span
                                                                          style={{ marginRight: 4 }}
                                                                      >
                                                                          {ADMIN_ACTIONS_EMOJI}
                                                                      </span>
                                                                      <span
                                                                          style={{
                                                                              textDecoration:
                                                                                  "underline",
                                                                          }}
                                                                      >
                                                                          Admin Actions
                                                                      </span>
                                                                  </Typography>
                                                              </ListItem>
                                                          )}
                                                      </List>
                                                  )}
                                              </React.Fragment>
                                          ))}
                                      </ul>
                                  </li>
                              ))}
                          </List>
                      )}
                  </Box>
              )}
        </Box>
    );
};

export default ProjectSidebar;
